import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { clearAdminSession, getAdminToken, getStoredAdminUser, type AdminUser } from "../lib/auth";
import { BrandLoader } from "./BrandLoader";
import { useDelayedLoader } from "../hooks/useDelayedLoader";

export function AuthGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<"loading" | "ok" | "need_login">("loading");
  const showLoader = useDelayedLoader(state === "loading");

  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      setState("need_login");
      return;
    }
    let cancelled = false;
    api<{ user?: AdminUser; email?: string; userId?: string }>("/api/v1/auth/me")
      .then((res) => {
        if (cancelled) return;
        const next: AdminUser = res.user ?? {
          id: String(res.userId ?? ""),
          email: String(res.email ?? ""),
        };
        localStorage.setItem("adminUser", JSON.stringify(next));
        setState("ok");
      })
      .catch(() => {
        if (cancelled) return;
        clearAdminSession();
        setState("need_login");
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (state === "loading") {
    return showLoader ? (
      <div className="auth-loading brand-loader-page" role="status">
        <BrandLoader size="lg" label="Verifying session…" />
      </div>
    ) : null;
  }

  if (state === "need_login") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

export function useAdminUser(): AdminUser | null {
  return getStoredAdminUser();
}
